import type { ComponentType } from 'react'
import { Outlet, NavLink, useParams } from 'react-router-dom'
import { cn } from '../lib/utils'
import { useAuthStore } from '../store/auth'
import {
  Users, Mail, UserCheck,
  Clock, Layout, Tag, FileText, Link, Settings as SettingsIcon, Trash2, MessageCircle,
  ClipboardList, Receipt, CalendarSync,
} from 'lucide-react'

const SETTINGS_NAV: { to: string; label: string; icon: ComponentType<{ className?: string }>; adminOnly?: boolean; ownerOnly?: boolean }[] = [
  { to: 'general', label: 'General', icon: SettingsIcon },
  { to: 'users', label: 'Users', icon: Users },
  { to: 'email-accounts', label: 'Email Accounts', icon: Mail, adminOnly: true },
  { to: 'email-templates', label: 'Email Templates', icon: FileText, adminOnly: true },
  { to: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, ownerOnly: true },
  { to: 'lead-sources', label: 'Lead Sources', icon: Link, adminOnly: true },
  { to: 'assignment-rules', label: 'Assignment Rules', icon: UserCheck, adminOnly: true },
  { to: 'sla', label: 'SLA Policies', icon: Clock, adminOnly: true },
  { to: 'custom-fields', label: 'Custom Fields', icon: Layout },
  { to: 'custom-statuses', label: 'Custom Statuses', icon: Tag },
  { to: 'views', label: 'Saved Views', icon: Layout, adminOnly: true },
  { to: 'document-recycle-bin', label: 'Document Recycle Bin', icon: Trash2, ownerOnly: true },
  { to: 'services', label: 'Services', icon: ClipboardList, ownerOnly: true },
  { to: 'invoicing', label: 'Invoicing', icon: Receipt, ownerOnly: true },
  { to: 'appointments-sync', label: 'Appointments Sync', icon: CalendarSync, adminOnly: true },
]

export function SettingsLayout() {
  const { companyId } = useParams<{ companyId: string }>()
  const { user } = useAuthStore()
  const isAdministrator = user?.role === 'administrator'
  const isOwnerOrAbove = user?.role !== 'sales_user'

  return (
    <div className="flex h-full">
      <aside className="w-48 flex-shrink-0 border-r border-gray-200 bg-white py-4 px-2">
        <p className="px-3 text-xs font-semibold text-gray-400 uppercase mb-2">Settings</p>
        <nav className="space-y-0.5">
          {SETTINGS_NAV.filter(item => (!item.adminOnly || isAdministrator) && (!item.ownerOnly || isOwnerOrAbove)).map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={`/company/${companyId}/settings/${to}`}
              className={({ isActive }) => cn('sidebar-link text-xs', isActive && 'active')}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="flex-1 overflow-auto bg-gray-50 p-6">
        <Outlet />
      </main>
    </div>
  )
}
